import { useMemo, useState } from 'react';
import {
  AddPriceBandIcon,
  Bin,
  EllipsisMenu,
  LinkButton,
  Prompt,
  SnackbarStatus,
  Table,
  Typography,
  useSnackbar,
  TextIcon,
  EditIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@weareopenr/component-library';
import { createColumnHelper } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import { usePricingCategories } from 'src/hooks/usePricingCategories';
import { INestedPricingCategory, IPricingCategory } from 'src/api/getPricingCategories/types';
import { createPricingCategory } from 'src/api/createPricingCategory/createPricingCategory';
import { InlineNameForm } from 'src/components/InlineNameForm/InlineNameForm';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';
import { QueryKeys } from 'src/hooks/QueryKeys';
import SkeletonContainer from 'src/components/SkeletonContainer';
import styles from './PricingSchemeCategoriesTab.module.scss';
import { useDeletePriceSchemeCategory } from 'src/hooks/useDeletePriceSchemeCategory';
import { updatePricingCategory } from 'src/api/updatePricingCategory/updatePricingCategory';
import { AddProductsToCategoryModal } from './AddProductsToCategoryModal';
import { MoveToCategoryPrompt } from '../MoveToCategoryPrompt';
import { useUpdatePricingCategory } from 'src/hooks/useUpdatePricingCategory';

interface ITableRow extends INestedPricingCategory {
  subcategoriesCount: number | string;
  isUncategorised: boolean;
  isCreateRow: boolean;
  children: ITableRow[];
}

const createRow: ITableRow = {
  isCreateRow: true,
  isUncategorised: false,
  category_id: '',
  name: '',
  parent_category_id: null,
  display_order: 0,
  products_assigned: 0,
  subcategoriesCount: 0,
  children: [],
};

const mapToTableRows = (
  categories: INestedPricingCategory[],
  isCreateOpen: boolean,
  parentCategoryId: string | null,
): ITableRow[] => {
  const rows = categories.map((category) => {
    const subCategoriesCount = category.children.length;
    const children = mapToTableRows(category.children, isCreateOpen, parentCategoryId);
    if (isCreateOpen && parentCategoryId !== null && parentCategoryId === category.category_id) {
      children.push(createRow);
    }
    return {
      ...category,
      isUncategorised: category.category_id === null,
      subcategoriesCount: category.category_id === null ? '-' : subCategoriesCount,
      isCreateRow: false,
      children,
    };
  });

  return rows;
};

const getSameLevelCategories = (category: ITableRow, categories: IPricingCategory[]) => {
  return categories
    .filter((cat) => cat.category_id !== null)
    .filter((cat) => cat.parent_category_id === category.parent_category_id)
    .sort((a, b) => a.display_order - b.display_order);
};
const isMoveable = (
  category: ITableRow,
  categories: IPricingCategory[],
): { canMoveUp: boolean; canMoveDown: boolean } => {
  const sameLevelCategories = categories ? getSameLevelCategories(category, categories) : [];

  if (sameLevelCategories.length === 0) {
    return {
      canMoveUp: false,
      canMoveDown: false,
    };
  }
  return {
    canMoveUp: sameLevelCategories[0].category_id !== category.category_id,
    canMoveDown: sameLevelCategories[sameLevelCategories.length - 1].category_id !== category.category_id,
  };
};

const columnHelper = createColumnHelper<ITableRow>();

export const PricingSchemeCategoriesTab = ({ priceSchemeId }: { priceSchemeId: string }) => {
  const { nestedCategories, isLoading, data, queryKey, orderedFlatCategories } = usePricingCategories(priceSchemeId);
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const deleteCategory = useDeletePriceSchemeCategory(priceSchemeId);
  const { mutate: updateCategory, isLoading: isMoveLoading } = useUpdatePricingCategory(priceSchemeId);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [parentCategoryId, setParentCategoryId] = useState<string | null>(null);
  const [categoryToEdit, setCategoryToEdit] = useState<ITableRow | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<ITableRow | null>(null);
  const [categoryToMove, setCategoryToMove] = useState<ITableRow | null>(null);
  const [categoryIdEditingName, setCategoryIdEditingName] = useState<string | null>(null);

  const pricingCategories = data ?? [];
  const areCrudActionsDisabled = isCreateOpen || !!categoryIdEditingName;

  const handleDelete = async () => {
    if (!categoryToDelete?.category_id) return;

    try {
      await deleteCategory.mutateAsync(categoryToDelete.category_id);
      await queryClient.invalidateQueries({ queryKey });
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `Category "${categoryToDelete.name}" has been deleted successfully`,
      });
    } catch (error) {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: `Failed to delete category "${categoryToDelete.name}": ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setCategoryToDelete(null);
    }
  };

  const handleMove = (category: ITableRow, direction: 'UP' | 'DOWN', pricingCategories: IPricingCategory[]) => {
    const sameLevelCategories = getSameLevelCategories(category, pricingCategories);
    const categoryIndex = sameLevelCategories.findIndex((cat) => cat.category_id === category.category_id);
    const adjacent = sameLevelCategories[direction === 'UP' ? categoryIndex - 1 : categoryIndex + 1];

    queryClient.setQueryData<IPricingCategory[]>(queryKey, (prev) =>
      prev.map((cat) => {
        if (cat.category_id === category.category_id) return { ...cat, display_order: adjacent.display_order };
        if (cat.category_id === adjacent.category_id) return { ...cat, display_order: category.display_order };
        return cat;
      }),
    );
  };

  const handleDeleteClick = (category: ITableRow) => setCategoryToDelete(category);

  const handleMoveToCategoryClick = (category: ITableRow) => setCategoryToMove(category);

  const handleMoveToCategory = (newParentId: string | null) => {
    if (!categoryToMove?.category_id) return;

    updateCategory(
      { categoryId: categoryToMove.category_id, newParentId },
      {
        onSuccess: () => {
          setCategoryToMove(null);
        },
      },
    );
  };

  const handleCreateSubCategoryClick = (categoryId: string, toggleExpanded: (value: boolean) => void) => {
    setParentCategoryId(categoryId);
    setIsCreateOpen(true);
    toggleExpanded(true);
  };

  const handleEditClick = (category: ITableRow) => setCategoryToEdit(category);

  const handleClose = () => {
    setIsCreateOpen(false);
    setParentCategoryId(null);
    setCategoryIdEditingName(null);
  };

  const handleInlineCategoryFormSubmit = async (name: string, original: ITableRow) => {
    if (original.isCreateRow) {
      await createPricingCategory(priceSchemeId, [{ name, parent_category_id: parentCategoryId }]);
    } else {
      await updatePricingCategory({ priceSchemeId, categoryId: original.category_id, newName: name });
    }

    await queryClient.invalidateQueries({
      queryKey: [getBrandId(), QueryKeys.pricingCategories, priceSchemeId],
    });
  };

  const tableData = useMemo(() => {
    const rows = mapToTableRows(nestedCategories, isCreateOpen, parentCategoryId);
    if (isCreateOpen && parentCategoryId === null) {
      return [...rows, createRow];
    }
    return rows;
  }, [nestedCategories, isCreateOpen, parentCategoryId]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Pricing Categories',
        cell: ({ row: { original }, getValue }) => {
          if (original.isCreateRow || (original.category_id === categoryIdEditingName && !original.isUncategorised)) {
            return (
              <InlineNameForm
                onSubmit={(name) => handleInlineCategoryFormSubmit(name, original)}
                onClose={handleClose}
                existingNames={pricingCategories.filter((c) => c.name !== original.name).map((c) => c.name)}
                defaultValue={original.isCreateRow ? '' : original.name}
              />
            );
          }
          if (original.isUncategorised) {
            return <span className={styles.uncategorised}>{getValue()}</span>;
          }
          return getValue();
        },
        footer: () => (
          <LinkButton onClick={() => setIsCreateOpen(true)} disabled={areCrudActionsDisabled}>
            + Create Category
          </LinkButton>
        ),
      }),
      columnHelper.accessor('subcategoriesCount', {
        header: 'Subcategories',
        size: 150,
        cell: ({ row: { original }, getValue }) => {
          if (original.isCreateRow) return null;
          if (original.isUncategorised) {
            return <span className={styles.uncategorised}>{getValue()}</span>;
          }
          return getValue();
        },
      }),
      columnHelper.accessor('products_assigned', {
        header: 'Products Assigned',
        size: 180,
        cell: ({ row: { original }, getValue }) => {
          if (original.isCreateRow) return null;
          if (original.isUncategorised) {
            return <span className={styles.uncategorised}>{getValue()}</span>;
          }
          return getValue();
        },
      }),
      columnHelper.display({
        id: 'action',
        size: 80,
        cell: ({ row: { original, toggleExpanded } }) => {
          if (original.isUncategorised || original.isCreateRow) return null;
          const { canMoveUp, canMoveDown } = isMoveable(original, data);
          return (
            <EllipsisMenu
              options={[
                {
                  id: 'create-sub-category',
                  name: 'Create Sub-category',
                  icon: AddPriceBandIcon,
                  isDisabled: areCrudActionsDisabled,
                  onOptionClick: () => handleCreateSubCategoryClick(original.category_id, toggleExpanded),
                },
                {
                  id: 'edit-products',
                  name: 'Edit Products',
                  icon: EditIcon,
                  isDisabled: areCrudActionsDisabled,
                  onOptionClick: () => handleEditClick(original),
                },
                {
                  id: 'edit-name',
                  name: 'Edit Name',
                  icon: TextIcon,
                  isDisabled: areCrudActionsDisabled,
                  onOptionClick: () => setCategoryIdEditingName(original.category_id),
                },
                { id: 'divider-2', isDivider: true },
                {
                  id: 'move-up',
                  name: 'Move up',
                  icon: ArrowUpIcon,
                  isDisabled: areCrudActionsDisabled || !canMoveUp,
                  onOptionClick: () => handleMove(original, 'UP', data),
                },
                {
                  id: 'move-down',
                  name: 'Move down',
                  icon: ArrowDownIcon,
                  isDisabled: areCrudActionsDisabled || !canMoveDown,
                  onOptionClick: () => handleMove(original, 'DOWN', data),
                },
                {
                  id: 'move-to-category',
                  name: 'Move to Category',
                  icon: EditIcon,
                  onOptionClick: () => handleMoveToCategoryClick(original),
                  isDisabled: areCrudActionsDisabled,
                },
                { id: 'divider-3', isDivider: true },
                {
                  id: 'delete-pricing-category',
                  name: 'Delete category',
                  hoverColour: 'tomato',
                  icon: Bin,
                  isDisabled: areCrudActionsDisabled,
                  onOptionClick: () => handleDeleteClick(original),
                },
              ]}
              variant="ghost"
              hoverStyle="high-contrast"
            />
          );
        },
      }),
    ],
    [pricingCategories, parentCategoryId, areCrudActionsDisabled, categoryIdEditingName],
  );

  if (isLoading) {
    return <SkeletonContainer hiddenTitle />;
  }

  return (
    <div className={styles['pricing-categories-tab']}>
      <Table
        className={styles['table-fill-height']}
        data={tableData}
        columns={columns}
        getSubRows={(row) =>
          row.isUncategorised || row.isCreateRow || row.children.length === 0 ? undefined : row.children
        }
        maxHeight="82vh"
      />
      {categoryToEdit && (
        <AddProductsToCategoryModal
          open={!!categoryToEdit}
          closeModal={() => setCategoryToEdit(null)}
          categoryName={categoryToEdit.name}
          categoryId={categoryToEdit.category_id}
          priceSchemeId={priceSchemeId}
        />
      )}
      {categoryToMove && (
        <MoveToCategoryPrompt
          open={!!categoryToMove}
          categoryName={categoryToMove.name}
          orderedCategories={orderedFlatCategories}
          category={categoryToMove}
          onClose={() => setCategoryToMove(null)}
          onSubmit={handleMoveToCategory}
          isSubmitting={isMoveLoading}
          confirmationMessage="I confirm I understand that moving this category may impact any price rules inherited from it or its parent."
          subtitle="This action will impact category attributes including moving subcategories and associated rules."
        />
      )}
      {categoryToDelete && (
        <Prompt
          open
          promptHeading={`Are you sure you want to delete "${categoryToDelete.name}"?`}
          closePrompt={() => setCategoryToDelete(null)}
          size="small"
          backButton={true}
          backButtonText="Cancel"
          buttonColour="tomato"
          buttonText="Delete"
          buttonType="button"
          buttonHandler={handleDelete}
          isSubmitting={deleteCategory.isLoading}
        >
          <Typography>
            Deleting this category will result in all associated products to move into “uncategorised” and rules to be
            permanently deleted
          </Typography>
          <br />
          <Typography>This action cannot be undone</Typography>
        </Prompt>
      )}
    </div>
  );
};
